export type SamplePrompt = {
  n: number;
  title: string;
  summary: string;
  text: string;
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
  { name: 'DeepSeek', url: () => 'https://chat.deepseek.com/' },
];

export const demoPrompt = {
  title: 'Daily Team Operations Summary',
  text: `“Act as an experienced operations supervisor and professional report-writing assistant. Help me organize the information provided below into a concise, objective daily team operations summary.

Include the reporting period, completed tasks, outstanding responsibilities, operational issues, relevant observations, and recommended follow-up actions.

Use clear professional language, distinguish confirmed information from assumptions, and do not invent missing details.

Information:
Reporting date: [DATE]
Team or department: [TEAM/DEPARTMENT]
Completed tasks: [COMPLETED TASKS]
Outstanding tasks: [OUTSTANDING TASKS]
Issues requiring attention: [RELEVANT ISSUES]
Follow-up responsibilities: [RESPONSIBLE PERSONS OR ROLES]
Deadlines: [RELEVANT DEADLINES]

Organize the result with clear headings and concise bullet points. If essential information is missing, identify it for review before producing the final version.”`,
};

export const demoOutput = {
  title: 'DAILY TEAM OPERATIONS SUMMARY',
  sections: [
    { heading: 'Reporting Information', lines: ['Reporting period', 'Team or department', 'Prepared by'] },
    { heading: 'Completed Activities', lines: ['Summary of completed responsibilities', 'Relevant outcomes and confirmed observations'] },
    { heading: 'Outstanding Tasks', lines: ['Pending responsibilities', 'Current status and next steps', 'Relevant deadlines, where provided'] },
    { heading: 'Issues Requiring Attention', lines: ['Description of reported issues', 'Known operational impact', 'Questions or information requiring verification'] },
    { heading: 'Follow-Up Actions', lines: ['Recommended next steps', 'Responsible roles, where specified', 'Deadlines and review points'] },
  ],
};

export const reviewChecklist = [
  { title: 'Check the Facts', desc: 'Confirm that dates, task statuses, names, figures, and sequences match the available records.' },
  { title: 'Verify Responsibilities', desc: 'Make sure assigned tasks, responsible roles, deadlines, and follow-up actions are accurate.' },
  { title: 'Review Missing Details', desc: 'Check whether any important information is incomplete, ambiguous, or inconsistent.' },
  { title: 'Protect Confidential Information', desc: 'Follow your organization’s policies on privacy, sensitive information, approved AI tools, and data handling. Do not enter confidential or personal information into an AI service unless its use is authorized.' },
  { title: 'Approve the Final Version', desc: 'Review and correct the draft before distributing it, making decisions based on it, or including it in official records.' },
];

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Daily Team Operations Summary',
    summary: 'Organize completed tasks, outstanding responsibilities, operational observations, and follow-up actions.',
    text: `Act as an experienced operations supervisor and professional report-writing assistant. Your task is to organize the information I provide into a clear, concise, and objective daily team operations summary.

Begin by establishing the reporting context, including the [REPORTING DATE], [REPORTING PERIOD], [TEAM OR DEPARTMENT], and [PERSON OR ROLE PREPARING THE SUMMARY].

Create a section titled Completed Activities. Summarize the [COMPLETED TASKS AND ACTIVITIES], including relevant outcomes and observations. Do not add accomplishments, figures, or events that were not supplied.

Create a section titled Outstanding Responsibilities. Organize the [PENDING TASKS], their known status, relevant [DEADLINES], and any dependencies that may affect completion. If the information does not identify a responsible person or deadline, mark it as requiring confirmation.

Create a section titled Operational Issues and Observations. Structure the [REPORTED ISSUES, OBSTACLES, OR UNUSUAL OBSERVATIONS] in neutral, professional language. Distinguish confirmed facts from interpretations and avoid unsupported conclusions.

Create a section titled Follow-Up Actions. Based on the information provided, list practical next steps, the [RESPONSIBLE PERSON OR ROLE], and the [TARGET DEADLINE] when known. Clearly identify any proposed actions that require supervisory approval.

Finish with an Items Requiring Review section. List missing details, inconsistent information, and points that should be verified before the summary is distributed or used for decisions.

Use clear headings, concise bullet points, and professional language appropriate for internal operational documentation.

Do not fabricate information, invent performance metrics, assume that a task has been completed, or make decisions on behalf of the supervisor.

If essential information is missing, ask me the necessary questions before preparing a final version.`,
  },
  {
    n: 2,
    title: 'Professional Task Delegation Message',
    summary: 'Structure instructions with a clear objective, expected outcome, assigned responsibility, deadline, and follow-up method.',
    text: `Act as an experienced team supervisor and workplace communication assistant. Your task is to help me write a clear, respectful, and professional task delegation message for [TEAM MEMBER NAME OR ROLE].

Begin by stating the objective of the task in one or two sentences: [TASK OBJECTIVE]. Briefly explain why it matters, using the context I provide: [OPERATIONAL CONTEXT].

Describe the expected outcome in concrete terms: [EXPECTED RESULT OR DELIVERABLE]. Include any quality standards, procedures, or constraints that apply: [RELEVANT STANDARDS OR CONSTRAINTS].

State the deadline [DEADLINE] and any intermediate checkpoints [CHECKPOINTS, IF ANY]. Identify the resources, tools, or people available to support the task: [AVAILABLE RESOURCES].

Explain the follow-up method, including how and when progress will be reviewed [FOLLOW-UP METHOD AND DATE], and who to contact if obstacles arise.

Use a tone that is [PREFERRED TONE]. Keep the message concise, avoid ambiguous instructions, and do not add responsibilities, deadlines, or consequences that I have not provided.

Finish by inviting the team member to confirm their understanding or ask questions.

If essential information is missing, ask me the necessary questions before preparing a final version.`,
  },
  {
    n: 3,
    title: 'Weekly Team Planning & Priority Review',
    summary: 'Organize upcoming tasks, competing priorities, deadlines, resource constraints, and proposed next steps.',
    text: `Act as an operations planning assistant for a team supervisor. Help me organize a weekly team plan and priority review for [TEAM OR DEPARTMENT] covering [WEEK OR DATE RANGE].

Start with a short overview of the week's main objectives: [WEEKLY OBJECTIVES].

Organize the following upcoming tasks into a priority list: [UPCOMING TASKS]. For each task, consider the [DEADLINES], dependencies, and [AVAILABLE TEAM MEMBERS OR ROLES]. Group tasks as High, Medium, or Low priority and briefly explain the reasoning based only on the information provided.

Create a section titled Resource Constraints. Summarize known limitations such as [STAFFING, EQUIPMENT, OR TIME CONSTRAINTS] and identify where competing priorities may conflict.

Create a section titled Proposed Weekly Schedule. Distribute priority tasks across the days of the week in a simple table, without assigning people who were not mentioned.

Create a section titled Risks and Open Questions. List items that need confirmation, approval, or additional information before the plan is finalized.

Finish with Proposed Next Steps for the supervisor to review.

Use clear headings and concise bullet points. Do not invent tasks, deadlines, or staffing details. If essential information is missing, ask me the necessary questions before preparing a final version.`,
  },
];
